import { useNavigation } from '@app/hooks/useNavigation';
import { Spinner } from '@components/ui';
import { useScanResultQuery } from '@dieterApi/scan/useScanResultQuery';
import PrintIcon from '@mui/icons-material/Print';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { ScanResultDisplay } from '../Dashboard/Topic/SubSections/TechnologyScan/ScanResultDisplay';
import { getDisplaySiteTitle } from '../Dashboard/Topic/SubSections/TechnologyScan/scanResultShared';

const fullPath = '/scan-result-print/:propertyId';

interface RouteParams {
  propertyId?: string;
}

export function ScanResultPrint() {
  const { t, i18n } = useTranslation();
  const match = useRouteMatch<RouteParams>(fullPath);
  const propertyId = match?.params?.propertyId;
  const [fetchScanResult, { data, loading, error }] = useScanResultQuery();
  const { navigation, setNavigation } = useNavigation();

  useEffect(() => {
    if (!propertyId) return;
    void fetchScanResult({ variables: { propertyId }, fetchPolicy: 'network-only' });
  }, [propertyId, fetchScanResult]);

  useEffect(() => {
    if (navigation.isPrintView) return;
    setNavigation((nav) => void (nav.isPrintView = true));
  }, [navigation, setNavigation]);

  useEffect(() => {
    return () => {
      setNavigation((nav) => void (nav.isPrintView = false));
    };
  }, [setNavigation]);

  const scanResult = data?.getScanResult;
  const websiteTitle = scanResult?.startUrl
    ? getDisplaySiteTitle(scanResult.startUrl)
    : t('route.scanresult.print.fallback_title');
  const scanDateLabel = scanResult?.createdAt
    ? new Date(scanResult.createdAt).toLocaleString(i18n.resolvedLanguage || i18n.language || undefined)
    : null;

  if (!propertyId) {
    return (
      <div className="p-6">
        <p>{t('route.scanresult.print.missing_id')}</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-6 print:max-w-none print:px-0 print:py-0">
      <div className="flex items-center justify-between gap-3 mb-4 print:hidden">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">
            {websiteTitle}
          </h1>
          {scanDateLabel && (
            <p className="text-sm text-gray-500 mt-1">
              {t('route.scanresult.print.scanned_at')}: {scanDateLabel}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary-root text-white hover:bg-primary-700"
        >
          <PrintIcon fontSize="small" />
          {t('route.scanresult.print.button')}
        </button>
      </div>

      {loading && !scanResult && (
        <div className="flex items-center justify-center py-16">
          <Spinner size="large" />
        </div>
      )}

      {error && !scanResult && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3">
          {t('route.scanresult.print.load_error')}
        </div>
      )}

      {!loading && !scanResult && !error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-4 py-3">
          {t('route.scanresult.print.not_found')}
        </div>
      )}

      {scanResult && (
        <div className="bg-white rounded-lg print:rounded-none">
          <ScanResultDisplay
            data={scanResult}
            startUrl={scanResult.startUrl || ''}
            loading={!scanResult.scanFinished}
            noScansLeft={false}
            timedOut={false}
            forceExpandAll
          />
        </div>
      )}
    </div>
  );
}
